/**
 * Queries publiques pour donoor.org (pages /donate, /c/, /a/, /b/, etc.).
 *
 * Utilisent la factory avec `allowedWithoutOrg: true` car aucune org
 * n'est attachée à la session (les pages publiques inferent l'org depuis
 * le slug / QR code dans l'URL).
 *
 * Note : ces queries fonctionnent côté client uniquement. Pour le SSR
 * (useFetch dans setup()), continuer d'utiliser useFetch / $fetch Nuxt
 * directement avec les types @donoor/api-contract pour la type-safety.
 */
import {
  defineOrgQuery,
  defineOrgMutation,
  apiCall,
  STANDARD,
  STABLE,
} from "@donoor/data-layer";
import type {
  Campaign,
  CampaignSummary,
  MembershipPlan,
  MembershipGroup,
  MembershipOrder,
  Event,
  Organization,
  Transaction,
} from "@donoor/api-contract";

import {
  campaignsPublicApi,
  type CampaignDonorsResponse,
  type DonorSortBy,
} from "~/services/api/campaignsPublic";

// ─── Campaigns ──────────────────────────────────────────────────────
export const usePublicCampaignsList = defineOrgQuery<
  CampaignSummary[],
  { limit?: number; offset?: number; organizationSlug?: string }
>({
  key: (params) => [
    "public-campaigns",
    "list",
    params.organizationSlug ?? "all",
    params.limit ?? 10,
    params.offset ?? 0,
  ],
  fetch: async (_, params, { signal }) => {
    const result = await apiCall<{
      success: true;
      data: CampaignSummary[];
    }>("/api/campaigns/public", {
      query: {
        limit: params.limit ?? 10,
        offset: params.offset ?? 0,
        organizationSlug: params.organizationSlug,
      },
      signal,
      skipAuth: true,
      skipOrganizationId: true,
    });
    return result.data;
  },
  cache: STABLE,
  allowedWithoutOrg: true,
});

export const usePublicCampaignBySlug = defineOrgQuery<Campaign, { slug: string }>({
  key: (params) => ["public-campaigns", "detail", params.slug],
  fetch: async (_, params, { signal }) => {
    const result = await apiCall<{ success: true; data: Campaign }>(
      `/api/campaigns/public/slug/${params.slug}`,
      { signal, skipAuth: true, skipOrganizationId: true },
    );
    return result.data;
  },
  cache: STABLE,
  allowedWithoutOrg: true,
});

/**
 * Donateurs paginés d'une campagne publique.
 *
 * NOTE : volontairement non factorisé via `defineOrgQuery` car le controller
 * `useCampaignPublicController` a besoin :
 *   - de switcher dynamiquement le tri ("recent" / "highest") avec un
 *     cache séparé par tri (pour switch instantané sans loading)
 *   - de pousser des optimistic updates dans le cache via
 *     `queryClient.setQueryData` au reçu d'un WebSocket `campaign:donation`
 *
 * Le controller crée son propre `useQuery` qui appelle ce service. La
 * convention de queryKey reste `["campaign-donors", campaignId, sortBy]`
 * pour rester réutilisable côté setQueryData.
 */
export const campaignDonorsKey = (
  campaignId: number | string | undefined,
  sortBy: DonorSortBy,
) => ["campaign-donors", campaignId, sortBy] as const;

export async function fetchCampaignDonors(
  campaignId: number | string,
  params: { limit: number; offset: number; sortBy: DonorSortBy },
  signal?: AbortSignal,
): Promise<CampaignDonorsResponse> {
  return campaignsPublicApi.donors(campaignId, params, signal);
}

// ─── Memberships ────────────────────────────────────────────────────
export const usePublicMembershipPlan = defineOrgQuery<
  MembershipPlan,
  { slug: string }
>({
  key: (params) => ["public-memberships", "plan", params.slug],
  fetch: async (_, params, { signal }) => {
    const result = await apiCall<{ success: true; data: MembershipPlan }>(
      `/api/public/memberships/${params.slug}`,
      { signal, skipAuth: true, skipOrganizationId: true },
    );
    return result.data;
  },
  cache: STANDARD,
  allowedWithoutOrg: true,
});

export const usePublicMembershipGroup = defineOrgQuery<
  MembershipGroup,
  { slug: string }
>({
  key: (params) => ["public-memberships", "group", params.slug],
  fetch: async (_, params, { signal }) => {
    const result = await apiCall<{ success: true; data: MembershipGroup }>(
      `/api/public/memberships/groups/${params.slug}`,
      { signal, skipAuth: true, skipOrganizationId: true },
    );
    return result.data;
  },
  cache: STANDARD,
  allowedWithoutOrg: true,
});

// ─── Events ─────────────────────────────────────────────────────────
export const usePublicEvent = defineOrgQuery<
  Event,
  { slug: string; preview?: boolean }
>({
  key: (params) => ["public-events", "detail", params.slug, params.preview ?? false],
  fetch: async (_, params, { signal }) => {
    const result = await apiCall<{ success: true; data: Event }>(
      `/api/public/events/${params.slug}`,
      {
        query: { preview: params.preview },
        signal,
        skipAuth: true,
        skipOrganizationId: true,
      },
    );
    return result.data;
  },
  cache: STANDARD,
  allowedWithoutOrg: true,
});

// ─── Organizations ──────────────────────────────────────────────────
export const usePublicOrganization = defineOrgQuery<
  Organization,
  { slug: string }
>({
  key: (params) => ["public-organizations", "detail", params.slug],
  fetch: async (_, params, { signal }) => {
    const result = await apiCall<{ success: true; data: Organization }>(
      `/api/organizations/public/slug/${params.slug}`,
      { signal, skipAuth: true, skipOrganizationId: true },
    );
    return result.data;
  },
  cache: STABLE,
  allowedWithoutOrg: true,
});

// ─── Mutations donation ──────────────────────────────────────────────
export const useCreatePaymentIntent = defineOrgMutation<
  { payment_intent_id: string; client_secret: string },
  {
    amount: number;
    organization_id: number | string;
    currency?: string;
    donor_email?: string;
    donor_name?: string;
    campaign_id?: number | string;
    qr_code_id?: number | string;
    idempotencyKey: string;
  }
>({
  mutate: async (_, body) => {
    const result = await apiCall<{
      success: true;
      data: { payment_intent_id: string; client_secret: string };
    }>("/api/public/transactions/create-payment", {
      method: "POST",
      body: {
        amount: body.amount,
        organization_id: body.organization_id,
        currency: body.currency ?? "eur",
        donor_email: body.donor_email,
        donor_name: body.donor_name,
        campaign_id: body.campaign_id,
        qr_code_id: body.qr_code_id,
      },
      headers: { "Idempotency-Key": body.idempotencyKey },
      skipAuth: true,
      skipOrganizationId: true,
    });
    return result.data;
  },
  allowedWithoutOrg: true,
});

export const useConfirmPayment = defineOrgMutation<
  Transaction,
  {
    payment_intent_id: string;
    client_secret?: string;
    amount: number;
    email?: string;
  }
>({
  mutate: async (_, body) => {
    const result = await apiCall<{ success: true; data: Transaction }>(
      "/api/public/transactions/confirm-payment",
      { method: "POST", body, skipAuth: true, skipOrganizationId: true },
    );
    return result.data;
  },
  allowedWithoutOrg: true,
});

export const useCreatePaypalOrder = defineOrgMutation<
  { order_id: string },
  {
    amount: number;
    organization_id: number | string;
    currency?: string;
    donor_email?: string;
    campaign_id?: number | string;
    idempotencyKey: string;
  }
>({
  mutate: async (_, body) => {
    const result = await apiCall<{ success: true; data: { order_id: string } }>(
      "/api/public/paypal/create-order",
      {
        method: "POST",
        body: {
          amount: body.amount,
          organization_id: body.organization_id,
          currency: body.currency ?? "eur",
          donor_email: body.donor_email,
          campaign_id: body.campaign_id,
        },
        headers: { "Idempotency-Key": body.idempotencyKey },
        skipAuth: true,
        skipOrganizationId: true,
      },
    );
    return result.data;
  },
  allowedWithoutOrg: true,
});

export const useCapturePaypalOrder = defineOrgMutation<
  Transaction,
  { orderId: string }
>({
  mutate: async (_, params) => {
    const result = await apiCall<{ success: true; data: Transaction }>(
      `/api/public/paypal/capture/${params.orderId}`,
      { method: "POST", body: {}, skipAuth: true, skipOrganizationId: true },
    );
    return result.data;
  },
  allowedWithoutOrg: true,
});

// ─── Memberships orders (public) ────────────────────────────────────
export const useCreateMembershipOrder = defineOrgMutation<
  MembershipOrder,
  {
    slug: string;
    plan_id: number | string;
    email: string;
    first_name?: string;
    last_name?: string;
    quantity?: number;
    options?: (number | string)[];
    custom_fields?: Record<string, unknown>;
    promo_code?: string;
  }
>({
  mutate: async (_, body) => {
    const { slug, ...rest } = body;
    const result = await apiCall<{ success: true; data: MembershipOrder }>(
      `/api/public/memberships/${slug}/create-order`,
      {
        method: "POST",
        body: rest,
        skipAuth: true,
        skipOrganizationId: true,
      },
    );
    return result.data;
  },
  allowedWithoutOrg: true,
});

export const useCreateFreeMembershipOrder = defineOrgMutation<
  MembershipOrder,
  {
    slug: string;
    email: string;
    first_name?: string;
    last_name?: string;
    custom_fields?: Record<string, unknown>;
  }
>({
  mutate: async (_, body) => {
    const { slug, ...rest } = body;
    const result = await apiCall<{ success: true; data: MembershipOrder }>(
      `/api/public/memberships/${slug}/create-free-order`,
      {
        method: "POST",
        body: rest,
        skipAuth: true,
        skipOrganizationId: true,
      },
    );
    return result.data;
  },
  allowedWithoutOrg: true,
});
